import {
    Box,
    Button,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "app/providers/AuthProvider";
import {
    EmptyState,
    FilterBar,
    FlussPageHeader,
    FlussStatusChip,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { useFlussCable } from "modules/fluss/hooks/useFlussCable";
import { flussApi } from "modules/fluss/services/fluss-api";
import { flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";
import { canOperateFluss } from "modules/fluss/utils/permissions";

const FlussMonitorPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const canOperate = canOperateFluss(user);
    const [tab, setTab] = useState(0);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const instances = useQuery({
        queryKey: ["fluss", "monitor", "instances", search, status],
        queryFn: () => flussApi.listInstances({ search: search || undefined, status: status || undefined }),
        enabled: canOperate,
    });
    const incidents = useQuery({
        queryKey: ["fluss", "monitor", "incidents"],
        queryFn: () => flussApi.listIncidents(),
        enabled: canOperate,
    });

    useFlussCable({
        monitor: canOperate,
        onReceived: () => queryClient.invalidateQueries({ queryKey: ["fluss", "monitor"] }),
    });
    const formatDate = (value?: string | null) =>
        value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "short" }).format(new Date(value)) : "—";

    if (!canOperate) return <Navigate to="/home/fluss/requests" replace />;

    return (
        <Box className="fluss-page fluss-page--dense">
            <Stack gap={2}>
                <FlussPageHeader
                    title={t("fluss.monitor.title", { defaultValue: "Fluss Monitor" })}
                    subtitle={t("fluss.monitor.subtitle", { defaultValue: "Observe and operate process instances." })}
                />
                <Tabs value={tab} onChange={(_, value) => setTab(value)}>
                    <Tab label={t("fluss.monitor.instances_tab", { defaultValue: "Instances" })} />
                    <Tab label={t("fluss.monitor.incidents_tab", { count: incidents.data?.length || 0, defaultValue: "Incidents ({{count}})" })} />
                </Tabs>
                {tab === 0 ? (
                    <>
                        <FilterBar search={search} onSearch={setSearch}>
                            <Select
                                size="small"
                                value={status}
                                displayEmpty
                                onChange={(event) => setStatus(event.target.value)}
                                inputProps={{ "aria-label": String(t("fluss.monitor.status_filter", { defaultValue: "Status filter" })) }}
                                sx={{ minWidth: 180 }}
                            >
                                <MenuItem value="">{t("fluss.common.all_statuses", { defaultValue: "All statuses" })}</MenuItem>
                                {["created", "running", "waiting", "completed", "cancelled", "incident", "suspended"].map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {t(`fluss.status.${value}`, { defaultValue: value })}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FilterBar>
                        {!instances.isLoading && !instances.data?.length ? (
                            <EmptyState
                                title={t("fluss.monitor.empty_title_v2", { defaultValue: "No instances yet." })}
                                description={t("fluss.monitor.empty_description_v2", { defaultValue: "Started processes will appear here." })}
                                actionLabel={t("fluss.monitor.empty_action", { defaultValue: "My requests" })}
                                onAction={() => navigate("/home/fluss/requests")}
                            />
                        ) : (
                            <Paper variant="outlined">
                                <List disablePadding>
                                    {(instances.data || []).map((instance) => (
                                        <ListItemButton
                                            key={instance.id}
                                            divider
                                            onClick={() => navigate(`/home/fluss/monitor/${instance.id}`)}
                                        >
                                            <ListItemText
                                                primary={flussDefinitionLabel(t, instance.definition_name)}
                                                secondary={
                                                    <Stack component="span" direction={{ xs: "column", md: "row" }} gap={{ xs: 0, md: 2 }}>
                                                        <Typography component="span" variant="body2">
                                                            {instance.start_failure_reason || instance.current_node_label || "—"}
                                                        </Typography>
                                                        <Typography component="span" variant="body2" color="text.secondary">
                                                            {t("fluss.monitor.version", { version: instance.definition_version, defaultValue: "Version {{version}}" })}
                                                        </Typography>
                                                        <Typography component="span" variant="body2" color="text.secondary">
                                                            {t("fluss.monitor.updated_at", { date: formatDate(instance.updated_at), defaultValue: "Updated {{date}}" })}
                                                        </Typography>
                                                    </Stack>
                                                }
                                            />
                                            <FlussStatusChip status={instance.status} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </>
                ) : !incidents.isLoading && !incidents.data?.length ? (
                    <EmptyState
                        title={t("fluss.incidents.empty_title", { defaultValue: "No open incidents" })}
                        description={t("fluss.incidents.empty_description", { defaultValue: "The engine has no blocked process tokens." })}
                    />
                ) : (
                    <Paper variant="outlined">
                        <List disablePadding>
                            {(incidents.data || []).map((incident) => (
                                <ListItemButton
                                    key={incident.id}
                                    divider
                                    onClick={() => navigate(`/home/fluss/monitor/${incident.instance_id}`)}
                                >
                                    <ListItemText
                                        primary={flussDefinitionLabel(t, incident.definition_name)}
                                        secondary={
                                            <Stack component="span" gap={0.25}>
                                                <Typography component="span" variant="body2">
                                                    {incident.node_label || incident.element_id || "—"}
                                                </Typography>
                                                <Typography component="span" variant="body2" color="error">
                                                    {incident.error || incident.reason}
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                    <Button size="small">
                                        {t("fluss.incidents.open", { defaultValue: "Open" })}
                                    </Button>
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                )}
            </Stack>
        </Box>
    );
};

export default FlussMonitorPage;
